import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Label as LabelIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

import { tagsAPI, livresAPI } from '../services/api';
import LivreCard from '../components/LivreCard/LivreCard';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

function TagDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Sélectionner la locale date-fns selon la langue i18n
  const dateLocale = i18n.language === 'en' ? enUS : fr;

  const { data: tag, isLoading: tagLoading, error: tagError } = useQuery(
    ['tag', id],
    () => tagsAPI.getById(id).then(res => res.data.data),
    {
      enabled: !!id,
    }
  );

  // Récupérer les livres avec ce tag
  const { data: livresData, isLoading: livresLoading } = useQuery(
    ['livres-tag', id],
    () => livresAPI.getAll({ tag_id: id, limit: 100 }).then(res => res.data),
    {
      enabled: !!id,
    }
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const deleteTagMutation = useMutation(
    () => tagsAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tags']);
        toast.success(t('tagDetailPage.deleteSuccess'));
        navigate('/tags');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || t('tagDetailPage.deleteError'));
      }
    }
  );

  const handleEditClick = () => {
    navigate(`/tags/${id}/modifier`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteTagMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Fonctions pour LivreCard
  const handleView = (livreId) => {
    navigate(`/livres/${livreId}`);
  };

  const handleEdit = (livreId) => {
    navigate(`/livres/${livreId}/modifier`);
  };

  const handleDelete = (livre) => {
    // Pour l'instant, rediriger vers la page des livres avec confirmation
    if (window.confirm(t('tagDetailPage.deleteBookConfirm', { title: livre.titre }))) {
      console.log('Suppression du livre:', livre.id);
    }
  };

  const handleAuthorClick = (authorId) => {
    if (authorId) {
      navigate(`/auteurs/${authorId}`);
    }
  };

  const handleEditeurClick = (editeurId) => {
    if (editeurId) {
      navigate(`/editeurs/${editeurId}`);
    }
  };

  const handlePhotoClick = (livreId) => {
    navigate(`/livres/${livreId}`);
  };

  // Fonction pour convertir hex en rgba
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(25, 118, 210, ${alpha})`;
    const num = parseInt(hex.replace('#', ''), 16);
    const R = (num >> 16) & 255;
    const G = (num >> 8) & 255;
    const B = num & 255;
    return `rgba(${R}, ${G}, ${B}, ${alpha})`;
  };

  if (tagLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tagError) {
    return (
      <Alert severity="error">
        {t('tagDetailPage.loadingError', { message: tagError.message })}
      </Alert>
    );
  }

  if (!tag) {
    return (
      <Alert severity="info">
        {t('tagDetailPage.notFound')}
      </Alert>
    );
  }

  const livres = livresData?.data || [];
  const tagColor = tag.couleur || '#1976d2';

  return (
    <Box>
      {/* Header avec navigation */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <IconButton onClick={() => navigate('/tags')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              flexGrow: 1, 
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('tagDetailPage.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              size="small"
              sx={{ 
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('tagDetailPage.edit')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              size="small"
              sx={{ 
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('tagDetailPage.delete')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Détails de l'étiquette */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {/* Icône carrée avec couleur */}
        <Avatar
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            bgcolor: hexToRgba(tagColor, 0.1),
            border: `2px solid ${hexToRgba(tagColor, 0.3)}`,
          }}
        >
          <LabelIcon sx={{ fontSize: 32, color: tagColor }} />
        </Avatar>
        
        {/* Nom et nombre de livres */}
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                fontWeight: 700,
                fontSize: '24px',
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {tag.nom}
            </Typography>
            {/* Point de couleur */}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: tagColor,
              }}
            />
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {tag.nombre_livres === 1 
              ? t('tagDetailPage.bookCount', { count: tag.nombre_livres || 0 })
              : t('tagDetailPage.bookCountPlural', { count: tag.nombre_livres || 0 })}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      {tag.description && (
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            component="h3"
            sx={{ 
              mb: 1,
              fontWeight: 700,
              fontSize: '16px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('tagDetailPage.description')}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {tag.description}
          </Typography>
        </Box>
      )}

      {/* Métadonnées */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
          <Typography 
            variant="h6" 
            component="h3"
            sx={{ 
              fontSize: '12px',
              fontWeight: 600,
              color: 'oklch(0.551 0.027 264.364)',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('tagDetailPage.metadata')}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: { xs: 0, sm: 2 },
          ml: 0 
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '14px',
              fontWeight: 400,
              color: 'oklch(0.446 0.03 256.802)',
              mb: { xs: 0.5, sm: 0 },
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('tagDetailPage.createdAt')} {tag.created_at ? format(new Date(tag.created_at), 'dd MMMM yyyy', { locale: dateLocale }) : t('tagDetailPage.unknownDate')}
          </Typography>
          {tag.updated_at && tag.updated_at !== tag.created_at && (
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                color: 'oklch(0.446 0.03 256.802)',
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {t('tagDetailPage.updatedAt')} {format(new Date(tag.updated_at), 'dd MMMM yyyy', { locale: dateLocale })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Livres avec cette étiquette */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          component="h3"
          sx={{ 
            mb: 2,
            fontWeight: 700,
            fontSize: '16px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {t('tagDetailPage.booksWithTag')}
        </Typography>
        
        {livresLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : livres.length === 0 ? (
          <Alert severity="info">
            {t('tagDetailPage.noBooks')}
          </Alert>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)', 
              lg: 'repeat(4, 1fr)',
              xl: 'repeat(6, 1fr)'
            }, 
            gap: { xs: 2, md: 3 },
            width: '100%'
          }}>
            {livres.map((livre) => (
              <Box key={livre.id} sx={{ width: '100%', minWidth: 0 }}>
                <LivreCard
                  livre={livre}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAuthorClick={handleAuthorClick}
                  onEditeurClick={handleEditeurClick}
                  onPhotoClick={handlePhotoClick}
                  queryClient={queryClient}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('tagDetailPage.confirmDeleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('tagDetailPage.deleteConfirm', { name: tag?.nom })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('tagDetailPage.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteTagMutation.isLoading}
          >
            {deleteTagMutation.isLoading ? t('tagDetailPage.deleting') : t('tagDetailPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TagDetailPage;

